import { MoreHorizontalIcon, Redo2, Undo2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useStore as useZustand } from 'zustand/react';
import { useShallow } from 'zustand/react/shallow';
import { IconButton } from '~/components/atoms/IconButton';
import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Separator } from '~/components/ui/separator';
import { formatShortcut } from '~/lib/shortcut';
import { useStore } from '~/store';
import { redo, undo } from '~/store/temporalActions';

export function ToolbarRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex w-full flex-wrap items-center gap-1 px-1 py-0.5 text-xs">{children}</div>
  );
}

export function ToolSubPanel({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="absolute left-0 right-0 top-full z-20 mt-1 flex h-8 items-center gap-2 rounded-md border bg-card px-2 text-xs shadow-md">
      <span className="font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

export function Divider() {
  return <Separator orientation="vertical" className="mx-0.5 h-5" />;
}

export function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <span className="px-1 font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </span>
  );
}

export function UndoRedoGroup() {
  const [canUndo, canRedo] = useZustand(
    useStore.temporal,
    useShallow((s) => [s.pastStates.length > 0, s.futureStates.length > 0]),
  );
  return (
    <div className="flex items-center gap-1">
      <IconButton label={`Undo (${formatShortcut('mod+z')})`} onClick={undo} disabled={!canUndo}>
        <Undo2 />
      </IconButton>
      <IconButton
        label={`Redo (${formatShortcut('mod+shift+z')})`}
        onClick={redo}
        disabled={!canRedo}
      >
        <Redo2 />
      </IconButton>
    </div>
  );
}

export function MorePopover({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          aria-label={`${label} — more options`}
          variant="ghost"
          size="icon-xs"
          className="h-7"
        >
          <MoreHorizontalIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3">{children}</PopoverContent>
    </Popover>
  );
}
